import { version_timed } from "../version";

/**
 * Le diagnostic d'execution, en local.
 *
 * Ce module envoyait aussi chaque evenement a `api/mobile-audit/events`, une
 * route faite pour comprendre les ecrans blancs. Cette route n'existe plus cote
 * serveur : l'envoi echouait a chaque appel et n'etait rattrape que par un
 * `console.warn`. Il a ete retire, avec tout ce qui ne servait qu'a composer sa
 * charge — identifiants d'installation et de session, plateforme, gravite,
 * instantane du DOM.
 *
 * Un second envoi a suivi le meme sort le 03/09/2026 : celui vers
 * `monitoring_navigations`, qui deversait le diagnostic dans la table de
 * navigation sous forme de faux chemins `/__diag/runtime|...`. Il en composait
 * 68 % des ecritures et 11 557 valeurs distinctes par jour, contre 177 pour la
 * navigation reelle -- la chaine embarquait la version du build, donc chaque
 * deploiement creait un jeu de valeurs neuf. La table avait atteint
 * 12,5 millions de lignes et 3,7 Go.
 *
 * Ce module n'ecrit donc plus rien vers le serveur. Ce qui reste sert encore,
 * et sert au meme sujet : le journal local `__timed_runtime_diag_logs__`,
 * lisible sur l'appareil de qui rencontre le probleme, et l'etat des service
 * workers, qui est la piste principale quand un ecran reste blanc apres une
 * mise a jour.
 */

function normalizeText(value: string) {
    return value.replace(/\s+/g, " ").replace(/[?#&]/g, "_").trim();
}

function limitText(value: string, max: number) {
    return value.length > max ? value.slice(0, max) : value;
}


async function getServiceWorkerDetails() {
    if (!("serviceWorker" in navigator)) {
        return { count: 0, summary: "sw:none" };
    }

    try {
        const regs = await navigator.serviceWorker.getRegistrations();
        if (!regs.length) return { count: 0, summary: "sw:0" };

        const names = regs
            .map((reg) => reg.active?.scriptURL || reg.waiting?.scriptURL || reg.installing?.scriptURL || "")
            .filter(Boolean)
            .map((url) => {
                try {
                    return new URL(url).pathname;
                } catch (error) {
                    return url;
                }
            })
            .join(",");

        return {
            count: regs.length,
            summary: limitText(`sw:${regs.length}:${names}`, 80),
        };
    } catch (error) {
        return { count: null, summary: "sw:err" };
    }
}

export function persistDiagLog(key: string, entry: unknown) {
    try {
        const previous = window.localStorage.getItem(key);
        const entries = previous ? JSON.parse(previous) : [];
        const nextEntries = [...entries, entry].slice(-20);
        window.localStorage.setItem(key, JSON.stringify(nextEntries));
    } catch (error) {
        console.warn("[diag] failed to persist log", error);
    }
}

export async function logRuntimeDiagnostic(
    kind: string,
    rawDetails: string,
    extra: Record<string, unknown> = {}
) {
    const path = window.location.pathname;
    const sw = await getServiceWorkerDetails();
    const details = normalizeText(rawDetails || "unknown");
    const entry = {
        kind,
        version: version_timed,
        path,
        details: rawDetails,
        sw: sw.summary,
        href: window.location.href,
        ua: navigator.userAgent,
        at: new Date().toISOString(),
        extra,
    };

    persistDiagLog("__timed_runtime_diag_logs__", entry);
    console.error("[runtime-diag]", entry);

    // L'envoi vers `monitoring_navigations` a ete retire.
    //
    // Cette table suit la navigation ; le diagnostic s'y deversait sous forme
    // de faux chemins -- `/__diag/runtime|page-visible|v=...|p=...`. Au
    // 02/09/2026 il pesait 68 % des ecritures du jour, avec 11 557 valeurs
    // distinctes contre 177 pour la navigation reelle : la chaine embarquait la
    // version du build, donc chaque deploiement creait un jeu de valeurs neuf.
    // La table avait atteint 12,5 millions de lignes et 3,7 Go.
    //
    // Rien ne se perd cote poste : l'entree reste dans `localStorage` et dans
    // la console, ou elle etait deja ecrite avant cet envoi.
}

export async function logOneSignalDiagnostic(
    step: string,
    details: string,
    extra: Record<string, unknown> = {}
) {
    return logRuntimeDiagnostic(`onesignal-${step}`, details, extra);
}

export async function logServiceWorkerMigrationDiagnostic(step: string, details: string) {
    return logRuntimeDiagnostic(`sw-migration-${step}`, details);
}
